import {
  Box,
  CircularProgress,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { ArrowForward, Info, Description, ArrowBack, Article, Replay } from '@material-ui/icons'
import MUIDataTable from 'mui-datatables'
import React, { useState, useMemo, useRef } from 'react'
import Chart from 'react-apexcharts'
import { useNavigate, useParams } from 'react-router-dom'
import { fDateTime } from 'src/utils/formatTime'
import { usePipelines, usePipelineTasks, rerunPipelineTask, usePipeline } from '../../api/pipeline-task-history/list'
import ClickAwayTooltip from 'src/components/ClickAwayTooltip'
import { PATH_DASHBOARD } from '../../routes/paths'
import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

const useStyles = makeStyles((theme) => ({
  expandedCell: {
    boxShadow: theme.shadows[3]
      .split('),')
      .map((s) => `inset ${s}`)
      .join('),'),
  },
}))

const formatDuration = (milliseconds) => {
  const seconds = Math.floor(milliseconds / 1000)
  if (seconds === 0) {
    return `${milliseconds}ms`
  }
  if (seconds < 60) {
    return `${seconds}s ${milliseconds % 1000}ms`
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`
  }

  return `${minutes}m ${remainingSeconds}s`
}

const TaskDetailsDialog = ({ open, onClose, task }) => {
  if (!task) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Task Details</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle1">Message JSON</Typography>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(task.message_json, null, 2)}
            </pre>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const LogDialog = ({ open, onClose, logData, loading, taskId }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Task Log {taskId ? `- Task ${taskId}` : ''}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
              {logData || 'No log data available'}
            </pre>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

const ConfirmationDialog = ({ open, onClose, onConfirm, title, content }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{content}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="primary" autoFocus>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const PipelineTaskTable = function () {
  const classes = useStyles()
  const { pipelineId } = useParams()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  // We know that the number of tasks is small, so we can set page size to 1000 for summary to be calculated on frontend
  const [page_size, setPageSize] = useState(1000)
  const [selectedTask, setSelectedTask] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [logOpen, setLogOpen] = useState(false)
  const [logData, setLogData] = useState('')
  const [logLoading, setLogLoading] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [taskToRerun, setTaskToRerun] = useState(null)
  const [order_by, setOrderBy] = useState('enqueued_at')
  const [order_direction, setOrderDirection] = useState('desc')

  const tasksApi = usePipelineTasks(pipelineId, page, page_size, order_by, order_direction)
  const { status, data = {} } = tasksApi.query
  const { results = [], count = 0 } = data

  const pipelineApi = usePipeline(pipelineId)
  const { data: pipeline } = pipelineApi

  // Store chart data in a ref to calculate only once (not on ordering changes)
  const chartDataRef = useRef(null)

  // Calculate chart data only once when results first become available
  if (results.length > 0 && chartDataRef.current === null) {
    const costMap = {}
    const timeMap = {}
    let qTime = 0

    // For top 5 lists - individual tasks
    const taskCosts = []
    const taskTimes = []

    results.forEach((task) => {
      const label = task.actor_name || task.queue_name || 'Unknown'

      // Cost
      const cost = task.total_cost_in_usd || 0
      costMap[label] = (costMap[label] || 0) + cost

      // Track individual task costs for top 5
      if (cost > 0) {
        taskCosts.push({
          id: task.id,
          actor_name: task.actor_name || 'Unknown',
          cost: cost,
        })
      }

      // Time
      if (task.started_at && task.completed_at) {
        const duration = new Date(task.completed_at).getTime() - new Date(task.started_at).getTime()
        if (duration > 0) {
          timeMap[label] = (timeMap[label] || 0) + duration

          // Track individual task times for top 5
          taskTimes.push({
            id: task.id,
            actor_name: task.actor_name || 'Unknown',
            duration: duration,
          })
        }
      }

      // Queue Time
      if (task.queue_time) {
        qTime += task.queue_time
      }
    })

    // Calculate totals for percentages
    const totalCost = taskCosts.reduce((sum, t) => sum + t.cost, 0)
    const totalTime = taskTimes.reduce((sum, t) => sum + t.duration, 0)

    // Sort and get top 5 with percentages
    const top5Cost = taskCosts
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 5)
      .map((t) => ({ ...t, percentage: totalCost > 0 ? (t.cost / totalCost) * 100 : 0 }))
    const top5Time = taskTimes
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 5)
      .map((t) => ({ ...t, percentage: totalTime > 0 ? (t.duration / totalTime) * 100 : 0 }))

    chartDataRef.current = {
      chartData: {
        cost: {
          series: Object.values(costMap),
          options: {
            labels: Object.keys(costMap),
            tooltip: {
              y: {
                formatter: (val) => `$${val.toFixed(6)}`,
              },
            },
            legend: { show: false },
          },
        },
        time: {
          series: Object.values(timeMap),
          options: {
            labels: Object.keys(timeMap),
            tooltip: {
              y: {
                formatter: (val) => formatDuration(val),
              },
            },
            legend: { show: false },
          },
        },
      },
      totalQueueTime: qTime,
      top5Cost,
      top5Time,
    }
  }

  const { chartData, totalQueueTime, top5Cost, top5Time } = chartDataRef.current || {
    chartData: { cost: { series: [], options: { labels: [] } }, time: { series: [], options: { labels: [] } } },
    totalQueueTime: 0,
    top5Cost: [],
    top5Time: [],
  }

  const handleViewDetails = (task) => {
    setSelectedTask(task)
    setDetailsOpen(true)
  }

  const handleViewLog = async (task) => {
    setSelectedTaskId(task.id)
    setLogOpen(true)
    setLogLoading(true)
    setLogData('')

    try {
      const { data } = await axios.get(`${API_BASE_URL}/pipeline-task-history/tasks/log`, {
        params: { id: task.id },
        responseType: 'text',
      })
      setLogData(data)
    } catch (error) {
      console.error('Error fetching log:', error)
      setLogData('Error loading log data. Please try again.')
    } finally {
      setLogLoading(false)
    }
  }

  const handleRerunClick = (task) => {
    setTaskToRerun(task)
    setConfirmOpen(true)
  }

  const handleConfirmRerun = async () => {
    if (!taskToRerun) return

    try {
      await rerunPipelineTask(pipelineId, taskToRerun.id)
      tasksApi.refresh()
    } catch (error) {
      console.error('Error rerunning task:', error)
    } finally {
      setConfirmOpen(false)
      setTaskToRerun(null)
    }
  }

  const handleOrderChange = (changedColumn, direction) => {
    const columnMapping = {
      id: 'id',
      queue_name: 'queue_name',
      actor_name: 'actor_name',
      enqueued_at: 'enqueued_at',
      started_at: 'started_at',
      completed_at: 'completed_at',
      state: 'state',
      pipeline: 'pipeline',
      queue_time: 'queue_time',
      processing_time: 'processing_time',
      ai_cost_in_usd: 'ai_cost_in_usd',
      compute_cost_in_usd: 'compute_cost_in_usd',
      total_cost_in_usd: 'total_cost_in_usd',
      created_at: 'created_at',
    }

    const newOrderBy = columnMapping[changedColumn] || 'enqueued_at'
    setOrderBy(newOrderBy)
    setOrderDirection(direction)
  }

  const COLUMNS = [
    {
      name: 'details',
      label: 'Details',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title="View Details">
                <IconButton onClick={() => handleViewDetails(row)} size="small">
                  <Info />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Log">
                <IconButton onClick={() => handleViewLog(row)} size="small">
                  <Article />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rerun Task">
                <IconButton onClick={() => handleRerunClick(row)} size="small">
                  <Replay />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
    },
    {
      name: 'id',
      label: 'ID',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return <Typography>{row.id}</Typography>
        },
      },
    },
    {
      name: 'queue_name',
      label: 'Queue',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <Tooltip title={row.queue_name}>
              <Typography>{row.queue_name}</Typography>
            </Tooltip>
          )
        },
      },
    },
    {
      name: 'actor_name',
      label: 'Task Type',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <Tooltip title={row.actor_name}>
              <Typography>{row.actor_name}</Typography>
            </Tooltip>
          )
        },
      },
    },
    {
      name: 'state',
      label: 'Status',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return (
            <Chip
              label={row.state.toUpperCase()}
              color={
                row.state === 'completed'
                  ? 'success'
                  : row.state === 'failed'
                  ? 'error'
                  : row.state === 'started'
                  ? 'primary'
                  : 'default'
              }
            />
          )
        },
      },
    },
    {
      name: 'queue_time',
      label: 'Queue Time',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.queue_time) return '-'
          return <Typography>{formatDuration(row.queue_time)}</Typography>
        },
      },
    },
    {
      name: 'processing_time',
      label: 'Processing Time',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.processing_time) return '-'
          return <Typography>{formatDuration(row.processing_time)}</Typography>
        },
      },
    },
    {
      name: 'ai_cost_in_usd',
      label: 'AI Cost (USD)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.ai_cost_in_usd) return '-'
          return <Typography>${row.ai_cost_in_usd.toFixed(6)}</Typography>
        },
      },
    },
    {
      name: 'compute_cost_in_usd',
      label: 'Compute Cost (USD)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.compute_cost_in_usd) return '-'
          return <Typography>${row.compute_cost_in_usd.toFixed(6)}</Typography>
        },
      },
    },
    {
      name: 'total_cost_in_usd',
      label: 'Total Cost (USD)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          if (!row.total_cost_in_usd) return '-'
          return <Typography>${row.total_cost_in_usd.toFixed(6)}</Typography>
        },
      },
    },
    {
      name: 'created_at',
      label: 'Created At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.created_at ? fDateTime(row.created_at) : null
        },
      },
    },
    {
      name: 'enqueued_at',
      label: 'Enqueued At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.enqueued_at ? fDateTime(row.enqueued_at) : null
        },
      },
    },
    {
      name: 'started_at',
      label: 'Started At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.started_at ? fDateTime(row.started_at) : null
        },
      },
    },
    {
      name: 'completed_at',
      label: 'Completed At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = results[dataIndex]
          return row.completed_at ? fDateTime(row.completed_at) : null
        },
      },
    },
  ]

  switch (status) {
    case 'success':
      return (
        <>
          <Box mb={2} mt={2}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate(PATH_DASHBOARD.pipelines)} variant="outlined">
              Back to Pipelines
            </Button>
          </Box>

          {pipeline && (
            <Box mb={3}>
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Pipeline Summary
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                      <Stack spacing={1}>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Status:
                          </Typography>
                          <Chip
                            label={(pipeline.status || '').toUpperCase()}
                            size="small"
                            color={
                              pipeline.status === 'success'
                                ? 'success'
                                : pipeline.status === 'failed'
                                ? 'error'
                                : pipeline.status === 'processing'
                                ? 'primary'
                                : 'default'
                            }
                          />
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Duration:
                          </Typography>
                          <Typography variant="body2">
                            {pipeline.created_at && pipeline.completed_at
                              ? formatDuration(new Date(pipeline.completed_at) - new Date(pipeline.created_at))
                              : '-'}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Total Cost:
                          </Typography>
                          <Typography variant="body2">
                            ${pipeline.total_cost_in_usd ? pipeline.total_cost_in_usd.toFixed(6) : '0.000000'}
                          </Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Total Queue Time:
                          </Typography>
                          <Typography variant="body2">{formatDuration(totalQueueTime)}</Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                      <Stack spacing={1}>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Organization:
                          </Typography>
                          <Typography variant="body2">{pipeline.organization_name || '-'}</Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Org ID:
                          </Typography>
                          <Typography variant="body2">{pipeline.organization_id || '-'}</Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Person:
                          </Typography>
                          <Typography variant="body2">{pipeline.person_name || '-'}</Typography>
                        </Box>
                        <Box display="flex">
                          <Typography variant="body2" color="textSecondary" style={{ minWidth: 120 }}>
                            Person ID:
                          </Typography>
                          <Typography variant="body2">{pipeline.person_id || '-'}</Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            Files:
                          </Typography>
                          <Typography variant="body2" noWrap title={pipeline.file_name_1}>
                            1. {pipeline.file_name_1 || '-'}
                          </Typography>
                          <Typography variant="body2" noWrap title={pipeline.file_name_2}>
                            2. {pipeline.file_name_2 || '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" align="center" gutterBottom>
                        Total Cost by Task
                      </Typography>
                      <Chart
                        options={chartData.cost.options}
                        series={chartData.cost.series}
                        type="pie"
                        width="100%"
                        height={350}
                      />
                      {top5Cost.length > 0 && (
                        <Box mt={3}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom>
                            Top 5 Most Expensive Tasks
                          </Typography>
                          <Stack spacing={0.5}>
                            {top5Cost.map((task, index) => (
                              <Box
                                key={task.id}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ py: 0.5, borderBottom: index < top5Cost.length - 1 ? '1px solid #eee' : 'none' }}
                              >
                                <Typography variant="body2" color="textSecondary">
                                  {index + 1}. {task.actor_name}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  ${task.cost.toFixed(6)} ({task.percentage.toFixed(1)}%)
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" align="center" gutterBottom>
                        Total Time by Task
                      </Typography>
                      <Chart
                        options={chartData.time.options}
                        series={chartData.time.series}
                        type="pie"
                        width="100%"
                        height={350}
                      />
                      {top5Time.length > 0 && (
                        <Box mt={3}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="subtitle2" gutterBottom>
                            Top 5 Longest Running Tasks
                          </Typography>
                          <Stack spacing={0.5}>
                            {top5Time.map((task, index) => (
                              <Box
                                key={task.id}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ py: 0.5, borderBottom: index < top5Time.length - 1 ? '1px solid #eee' : 'none' }}
                              >
                                <Typography variant="body2" color="textSecondary">
                                  {index + 1}. {task.actor_name}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {formatDuration(task.duration)} ({task.percentage.toFixed(1)}%)
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
          <MUIDataTable
            title="Pipeline Tasks"
            data={results}
            columns={COLUMNS}
            options={{
              selectableRows: 'none',
              sortOrder: { name: order_by, direction: order_direction },
              expandableRows: false,
              print: false,
              viewColumns: true,
              download: false,
              serverSide: true,
              count: count,
              page: page - 1,
              rowsPerPage: page_size,
              rowsPerPageOptions: [10, 25, 50],
              onChangePage: (currentPage) => setPage(currentPage + 1),
              onChangeRowsPerPage: (numberOfRows) => {
                // setPageSize(numberOfRows)
                // setPage(1)
              },
              onColumnSortChange: handleOrderChange,
              filter: false,
            }}
          />
          <TaskDetailsDialog open={detailsOpen} onClose={() => setDetailsOpen(false)} task={selectedTask} />
          <LogDialog
            open={logOpen}
            onClose={() => setLogOpen(false)}
            logData={logData}
            loading={logLoading}
            taskId={selectedTaskId}
          />
          <ConfirmationDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleConfirmRerun}
            title="Confirm Rerun"
            content="Are you sure you want to rerun this task?"
          />
        </>
      )
    default:
      return <CircularProgress />
  }
}

export default PipelineTaskTable
